import axios from "axios";

/*
 * Camada de rede do totem de NPS. Porte de firmware/nps_kiosk/nps_api.cpp do
 * projeto konfidency.nps-kiosk. Os tres endpoints usados aqui sao publicos na
 * reviews-api (sem preValidation) e o CORS esta aberto, entao o tablet fala
 * direto com a API — nao ha proxy nem chave.
 */

const WEB_BASE_URL =
  process.env.REACT_APP_WEB_BASE_URL || "https://www.kfy.app";

const KIOSK_SOURCE = "kiosk";
const HTTP_TIMEOUT_MS = 10000;

export const KIOSK_OK = "OK";
export const KIOSK_ERR_NETWORK = "NETWORK"; /* transitorio: vale reenviar */
export const KIOSK_ERR_ALREADY = "ALREADY_SENT"; /* celular concluiu; nota gravada */
export const KIOSK_ERR_GONE = "REQUEST_GONE"; /* solicitacao sumiu; reemitir */
export const KIOSK_ERR_CLOSED = "SURVEY_CLOSED"; /* pesquisa inativa ou sem auto-create */

const apiBase = (customer) =>
  `${process.env.REACT_APP_API_URL}/${encodeURIComponent(customer)}`;

/*
 * request-by-user-id e get-or-create: reaproveitar o identificador faria o
 * totem funcionar uma unica vez. O nonce nunca pode ser persistido.
 */
function buildUserId() {
  const random =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;

  return `kiosk-web-${random.slice(0, 20)}`;
}

/*
 * Pergunta e diagnostico da pesquisa. A resposta ja traz npsQuestion, o que
 * evita depender da criacao de solicitacao para pintar a tela de coleta.
 */
export async function fetchSurveys(customer) {
  const { data } = await axios.get(`${apiBase(customer)}/nps/surveys/device`, {
    timeout: HTTP_TIMEOUT_MS,
  });

  return Array.isArray(data) ? data : [];
}

export function pickSurvey(surveys, surveyId) {
  if (!surveys.length) return null;
  if (surveyId) return surveys.find((survey) => survey._id === surveyId) || null;

  return surveys.find((survey) => survey.active) || null;
}

/*
 * Cria uma solicitacao. requestId nulo nao e falha de rede: significa pesquisa
 * inativa ou sem allowAutoCreateRequests, que e problema de configuracao e
 * precisa de uma tela propria.
 */
export async function createRequest(customer, surveyId) {
  const body = { userId: buildUserId() };
  if (surveyId) body.surveyId = surveyId;

  let data;
  try {
    ({ data } = await axios.post(
      `${apiBase(customer)}/nps/request-by-user-id`,
      body,
      { timeout: HTTP_TIMEOUT_MS }
    ));
  } catch (err) {
    return { result: KIOSK_ERR_NETWORK };
  }

  if (!data?.requestId) return { result: KIOSK_ERR_CLOSED, detail: data };

  return {
    result: KIOSK_OK,
    requestId: data.requestId,
    question: data.npsQuestion || "",
  };
}

/*
 * Grava a nota. partial e sempre true: um false marcaria a resposta como
 * concluida (grava sendDate) e a API passaria a recusar o complemento feito
 * pelo celular via QR Code.
 */
export async function sendRating(customer, requestId, rating) {
  try {
    await axios.post(
      `${apiBase(customer)}/nps/${requestId}`,
      { rating, partial: true, source: KIOSK_SOURCE },
      { timeout: HTTP_TIMEOUT_MS }
    );
    return KIOSK_OK;
  } catch (err) {
    const status = err.response?.status;
    const payload = err.response?.data;
    const body = typeof payload === "string" ? payload : JSON.stringify(payload || "");

    /* O celular pode ter concluido antes do reenvio chegar. A nota ja esta
     * gravada — insistir so geraria erro para sempre. */
    if (body.indexOf("full response already sent") >= 0) return KIOSK_ERR_ALREADY;

    /* NpsRequestClosedOrNotFoundError: a solicitacao sumiu. A nota NAO foi
     * gravada — quem chamou precisa reemitir, nao descartar. */
    if (status === 400) return KIOSK_ERR_GONE;

    return KIOSK_ERR_NETWORK;
  }
}

/*
 * O recipiente e descartavel, a nota nao: em REQUEST_GONE troca o requestId e
 * reenvia uma vez. Devolve o id efetivamente usado para quem chamou reancorar
 * a fila e reescrever o QR.
 */
export async function sendRatingResilient(customer, requestId, rating, nextRequestId) {
  const result = await sendRating(customer, requestId, rating);
  if (result !== KIOSK_ERR_GONE) return { result, requestId };

  const replacement = await nextRequestId();
  if (!replacement) return { result, requestId };

  return {
    result: await sendRating(customer, replacement, rating),
    requestId: replacement,
  };
}

export function buildSurveyUrl(customer, requestId, rating) {
  if (!requestId) return "";

  return `${WEB_BASE_URL}/nps/${encodeURIComponent(customer)}/${requestId}?npsRating=${rating}`;
}
